import { Redirect } from "@redotech/react-router-util/redirect";
import { useRequiredContext } from "@redotech/react-util/context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { OrderManagementPermissions } from "@redotech/redo-model/permissions";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { memo, ReactNode, useContext } from "react";
import { matchPath, Navigate, Outlet } from "react-router-dom";
import { orderFulfillmentAnalyticsRoutes } from "../../../analytics/order-fulfillment/route";
import { returnsClaimsAnalyticsRoutes } from "../../../analytics/returns-claims/route";
import { returnsProcessingAnalyticsRoutes } from "../../../analytics/returns-processing/route";
import {
  backwardsCompatibleOrderRoutes,
  outboundLabelRoutes,
  wmsRoutes,
} from "../../../order/route";
import { registrationRoutes } from "../../../registration/route";
import {
  claimRoutes,
  recycleRoutes,
  returnRoutes,
  warrantyRoutes,
} from "../../../return/route";
import { Page } from "../../page";
import { NavigationVersionContext } from "../navigation-version-context";
import { warehouseRoutes } from "./warehouse-routes";

// When NEW_NAVIGATION is enabled, redirect to the given new path; otherwise
// render children via Outlet so the old route continues to work as before.
const NavOrgRedirect = memo(function NavOrgRedirect({
  match,
  replaceValue,
}: {
  match: RegExp;
  replaceValue: string;
}) {
  const { newNavigationEnabled } = useContext(NavigationVersionContext);

  if (!newNavigationEnabled) {
    return <Outlet />;
  }

  return <Redirect match={match} replaceValue={replaceValue} />;
});

// Index redirect: when new nav is on, go to the new shipping root; otherwise
// stay in the legacy order-management tree.
const OrderManagementIndexRedirect = memo(
  function OrderManagementIndexRedirect() {
    const { newNavigationEnabled } = useContext(NavigationVersionContext);

    return newNavigationEnabled ? (
      <Redirect match={/order-management/} replaceValue="shipping" />
    ) : (
      <Redirect
        match={/order-management\/?$/}
        replaceValue="order-management/outbound-labels"
      />
    );
  },
);

const reportBreadcrumb = breadcrumbSlot();

const HIDE_PADDING_ROUTES: string[] = [
  "/orders",
  "/returns/*",
  "/registrations/*",
  "/claims/*",
  "/warranties/*",
  "/recycle/*",
  "/batch-history",
  "/bulk-fulfillments",
  "/eod-manifest",
  "/outbound-labels/*",
  "/orders/:orderId",
  "/recommerce/*",
  "/analytics/order-fulfillment",
  "/analytics/returns-claims",
  "/analytics/returns-claims/*",
  "/analytics/managed-returns",
  "/analytics/reports",
  "/outbound-labels/bad-warehouse-bins",
  "/wms/*",
  "/warehouse",
  "/warehouse/*",
];

function hidePadding(url: string): boolean {
  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/order-management${route}`, url),
  );
}

const FULLSCREEN_ROUTES: string[] = [
  "/outbound-labels/packing-slips/:packingSlipId",
  "/outbound-labels/personalized-slips/:personalizedSlipId",
  "/outbound-labels/pick-sessions",
  "/outbound-labels/pick-sessions/:pickSessionId",
  "/outbound-labels/pick-sessions/:pickSessionId/tote-scanning",
  "/outbound-labels/pick-sessions/:pickSessionId/route",
  "/outbound-labels/create-pick-session",
  "/wms/work-orders/detail/:workOrderId",
  "/warehouse",
  "/warehouse/*",
];

const HIDE_HEADER_ROUTES: string[] = [...FULLSCREEN_ROUTES];
function hideHeader(url: string): boolean {
  return HIDE_HEADER_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/order-management${route}`, url),
  );
}

const HIDE_NAV_ROUTES: string[] = [...FULLSCREEN_ROUTES];

function hideNav(url: string): boolean {
  return HIDE_NAV_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/order-management${route}`, url),
  );
}

export const orderManagementRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Order management" },
    async lazy() {
      const { Page } = await import("../../page");
      return {
        element: (
          <Page
            hideHeader={hideHeader}
            hideNavbar={hideNav}
            hidePadding={hidePadding}
          />
        ),
      };
    },
    children: [
      { index: true, element: <OrderManagementIndexRedirect /> },
      {
        path: "warranties",
        element: (
          <NavOrgRedirect match={/order-management/} replaceValue="returns" />
        ),
        handle: { permission: OrderManagementPermissions.MANAGE_WARRANTY },
        children: warrantyRoutes,
      },
      {
        path: "returns",
        element: (
          <NavOrgRedirect match={/order-management/} replaceValue="returns" />
        ),
        handle: { permission: OrderManagementPermissions.VIEW_RETURN },
        children: returnRoutes,
      },
      {
        path: "claims",
        element: (
          <NavOrgRedirect match={/order-management/} replaceValue="returns" />
        ),
        handle: {
          permission: [
            OrderManagementPermissions.MANAGE_CARRIER_CLAIM,
            OrderManagementPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
          ],
        },
        children: claimRoutes,
      },
      {
        path: "registrations",
        element: (
          <NavOrgRedirect match={/order-management/} replaceValue="returns" />
        ),
        handle: { permission: OrderManagementPermissions.MANAGE_WARRANTY },
        children: registrationRoutes,
      },
      {
        path: "recycle",
        element: (
          <NavOrgRedirect match={/order-management/} replaceValue="returns" />
        ),
        handle: {
          permission: OrderManagementPermissions.MANAGE_RECYCLING_REQUESTS,
        },
        children: recycleRoutes,
      },
      {
        path: "classify-products",
        element: (
          <NavOrgRedirect match={/order-management/} replaceValue="shipping" />
        ),
        children: [
          {
            index: true,
            handle: { breadcrumb: "Classify products" },
            async lazy() {
              const { ClassifyProductsPage } = await import(
                "../../../order/classify-products-page"
              );
              return { element: <ClassifyProductsPage /> };
            },
          },
        ],
      },
      {
        path: "order-edits",
        element: (
          <NavOrgRedirect match={/order-management/} replaceValue="shipping" />
        ),
        children: [
          {
            index: true,
            handle: {
              breadcrumb: "Order editing",
              permission: OrderManagementPermissions.VIEW_ORDER,
            },
            async lazy() {
              const { OrderEditsPage } = await import(
                "../../../order-edits/order-edits-page"
              );
              return { element: <OrderEditsPage /> };
            },
          },
        ],
      },
      {
        path: "analytics/order-fulfillment",
        element: (
          <NavOrgRedirect match={/order-management/} replaceValue="shipping" />
        ),
        handle: { breadcrumb: "Analytics", breadcrumbNavigate: false },
        children: orderFulfillmentAnalyticsRoutes,
      },
      {
        path: "analytics/returns-claims",
        element: (
          <NavOrgRedirect match={/order-management/} replaceValue="returns" />
        ),
        handle: { breadcrumb: "Analytics", breadcrumbNavigate: false },
        children: returnsClaimsAnalyticsRoutes,
      },
      {
        path: "analytics/returns-processing",
        element: (
          <NavOrgRedirect match={/order-management/} replaceValue="returns" />
        ),
        handle: { breadcrumb: "Analytics", breadcrumbNavigate: false },
        children: returnsProcessingAnalyticsRoutes,
      },
      {
        path: "analytics/managed-returns",
        element: (
          <NavOrgRedirect match={/order-management/} replaceValue="returns" />
        ),
        handle: { breadcrumb: "Analytics", breadcrumbNavigate: false },
        children: [
          {
            index: true,
            handle: { breadcrumb: "Managed Returns" },
            async lazy() {
              const { RedoManagedReturnsPage } = await import(
                "@redotech/redo-merchant-managed-returns-app/redo-managed-returns-page"
              );
              return {
                Component: () => {
                  const team = useRequiredContext(TeamContext);
                  const isReFulfillmentEnabled =
                    team.settings.reFulfillment?.enabled ?? false;
                  return (
                    <ManagedReturnsAccessGuard>
                      <RedoManagedReturnsPage
                        showRefulfillment={isReFulfillmentEnabled}
                      />
                    </ManagedReturnsAccessGuard>
                  );
                },
              };
            },
          },
        ],
      },
      {
        path: "analytics/reports",
        element: (
          <NavOrgRedirect match={/order-management/} replaceValue="returns" />
        ),
        children: [
          {
            index: true,
            handle: {
              breadcrumb: reportBreadcrumb,
              permission: OrderManagementPermissions.VIEW_REPORTS,
            },
            async lazy() {
              const { ReportPage } = await import(
                "../../../analytics/reports/report"
              );
              return { element: <ReportPage breadcrumb={reportBreadcrumb} /> };
            },
          },
        ],
      },
      {
        path: "outbound-labels",
        element: (
          <NavOrgRedirect match={/order-management/} replaceValue="shipping" />
        ),
        children: outboundLabelRoutes,
      },
      {
        path: "wms",
        element: (
          <NavOrgRedirect match={/order-management/} replaceValue="shipping" />
        ),
        children: wmsRoutes,
      },
      {
        path: "warehouse",
        element: (
          <NavOrgRedirect match={/order-management/} replaceValue="shipping" />
        ),
        children: warehouseRoutes,
      },
    ],
  },
];

export const backwardsCompatibleOrderManagementRoutes: PermissionedRouteObject[] =
  [
    {
      path: "approvals",
      children: [
        {
          element: <Page />,
          children: [
            {
              index: true,
              element: (
                <Redirect
                  match={/approvals/}
                  replaceValue="order-management/approvals"
                />
              ),
            },
            {
              path: "*",
              element: (
                <Redirect
                  match={/approvals/}
                  replaceValue="order-management/approvals"
                />
              ),
            },
          ],
        },
      ],
    },
    {
      path: "warranties",
      children: [
        {
          element: <Page />,
          children: [
            {
              index: true,
              element: (
                <Redirect
                  match={/warranties/}
                  replaceValue="order-management/warranties"
                />
              ),
            },
            {
              path: "*",
              element: (
                <Redirect
                  match={/warranties/}
                  replaceValue="order-management/warranties"
                />
              ),
            },
          ],
        },
      ],
    },
    {
      path: "returns",
      children: [
        {
          element: <Page />,
          children: [
            {
              index: true,
              element: (
                <Redirect
                  match={/(?<!order-management\/)returns/}
                  replaceValue="order-management/returns"
                />
              ),
            },
            {
              path: "*",
              element: (
                <Redirect
                  match={/(?<!order-management\/)returns/}
                  replaceValue="order-management/returns"
                />
              ),
            },
          ],
        },
      ],
    },
    {
      path: "about-oms",
      children: [
        {
          element: <Page />,
          children: [
            {
              index: true,
              element: (
                <Redirect
                  match={/about-oms/}
                  replaceValue="order-management/outbound-labels/about-oms"
                />
              ),
            },
            {
              path: "*",
              element: (
                <Redirect
                  match={/about-oms/}
                  replaceValue="order-management/outbound-labels/about-oms"
                />
              ),
            },
          ],
        },
      ],
    },
    {
      path: "claims",
      children: [
        {
          element: <Page />,
          children: [
            {
              index: true,
              element: (
                <Redirect
                  match={/claims/}
                  replaceValue="order-management/claims"
                />
              ),
            },
            {
              path: "*",

              element: (
                <Redirect
                  match={/claims/}
                  replaceValue="order-management/claims"
                />
              ),
            },
          ],
        },
      ],
    },
    {
      path: "recycle",
      children: [
        {
          element: <Page />,
          children: [
            {
              index: true,
              element: (
                <Redirect
                  match={/recycle/}
                  replaceValue="order-management/recycle"
                />
              ),
            },
            {
              path: "*",
              element: (
                <Redirect
                  match={/recycle/}
                  replaceValue="order-management/recycle"
                />
              ),
            },
          ],
        },
      ],
    },
    {
      path: "returns/processing",
      children: [
        {
          element: <Page />,
          children: [
            {
              index: true,
              element: (
                <Redirect
                  match={/returns\/processing/}
                  replaceValue="order-management/warehouse/processing"
                />
              ),
            },
            {
              path: "*",
              element: (
                <Redirect
                  match={/returns\/processing/}
                  replaceValue="order-management/warehouse/processing"
                />
              ),
            },
          ],
        },
      ],
    },
    {
      path: "returns/processing-flow",
      children: [
        {
          element: <Page />,
          children: [
            {
              index: true,
              element: (
                <Redirect
                  match={/returns\/processing-flow/}
                  replaceValue="order-management/warehouse/processing-flow"
                />
              ),
            },
            {
              path: "*",
              element: (
                <Redirect
                  match={/returns\/processing-flow/}
                  replaceValue="order-management/warehouse/processing-flow"
                />
              ),
            },
          ],
        },
      ],
    },
    {
      path: "orders/*",
      element: <Page />,
      children: [{ children: backwardsCompatibleOrderRoutes }],
    },
  ];

const ManagedReturnsAccessGuard = memo(function ManagedReturnsAccessGuard({
  children,
}: {
  children: ReactNode;
}) {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);

  const isManagedReturnsEnabled = team.settings.redoManagedReturns?.enabled;

  if (!isManagedReturnsEnabled) {
    return <Navigate replace to={routes.returnsHref} />;
  }

  return <>{children}</>;
});
